import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { resetInformationSchema } from '../schemas/resetInformationSchema';
import { resetInformation } from '../api/user';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../hooks/useUser';
import LabeledTextField from '../components/LabeledTextField';
import UserPageLayout from '../components/UserPageLayout';
import { Box, Button, Alert, Typography, Stack } from '@mui/material';
import { formatDateShort } from '../utils/dateFormat';
import { NoticeModal } from '../components/Modals';

const ResetInformation = () => {
  const navigate = useNavigate();
  const { user, isLoading } = useUser();
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [noticeMsg, setNoticeMsg] = useState('');

  const showNotice = (msg) => {
    setNoticeMsg(msg);
    setNoticeOpen(true);
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    setError: setFormError,
  } = useForm({
    resolver: zodResolver(resetInformationSchema),
    defaultValues: {
      email: '',
      phoneNumber: '',
    },
  });

  useEffect(() => {
    if (user) {
      setValue('email', user.email || '');
      setValue('phoneNumber', user.phone_number || '');
    }
  }, [user, setValue]);

  const onSubmit = async (data) => {
    try {
      await resetInformation(data.email, data.phoneNumber);

      showNotice('基本情報が変更されました。');
      navigate('/mypage');
    } catch (err) {
      setFormError('root', {
        type: 'manual',
        message: 'お客様情報の変更に失敗しました',
      });

      console.error('Reset UserInformation Error:', err);
    }
  };

  return (
    <UserPageLayout title="お客様情報変更" maxWidth="sm">
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          maxWidth: 'none',
          boxShadow: 'none',
          minHeight: 'fit-content',
          padding: 0,
          mt: '5dvh',
          alignItems: 'flex-start',
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            氏名
          </Typography>
          <Typography
            variant="body1"
            sx={{
              p: 1.5,
            }}
          >
            {isLoading
              ? '読み込み中...'
              : user?.name || '名前が設定されていません'}
          </Typography>
        </Box>

        <Box sx={{ width: '100%' }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            登録日
          </Typography>
          <Typography variant="body1" sx={{ p: 1.5 }}>
            {isLoading
              ? '読み込み中...'
              : formatDateShort(user?.registered_at || '')}
          </Typography>
        </Box>

        <LabeledTextField
          label="メールアドレス"
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          placeholder="xxx@example.com"
          register={register}
          error={errors.email}
          disabled={isLoading}
        />

        <LabeledTextField
          label="電話番号"
          id="phoneNumber"
          name="phoneNumber"
          type="tel"
          autoComplete="tel"
          placeholder="電話番号を入力してください"
          register={register}
          error={errors.phoneNumber}
          disabled={isLoading}
        />

        {errors.root && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errors.root.message}
          </Alert>
        )}

        <Stack direction="column" spacing={2} sx={{ mt: 3, width: '100%' }}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 3, mb: 2, borderRadius: 5 }}
            disabled={isSubmitting || isLoading}
          >
            {isSubmitting ? '変更中...' : '変更を保存'}
          </Button>
          <Button
            variant="outlined"
            fullWidth
            color="primary"
            onClick={() => navigate('/mypage')}
            sx={{ borderRadius: 5 }}
            disabled={isSubmitting}
          >
            戻る
          </Button>
        </Stack>
      </Box>
      <NoticeModal
        open={noticeOpen}
        message={noticeMsg}
        onClose={() => setNoticeOpen(false)}
      />
    </UserPageLayout>
  );
};

export default ResetInformation;
